























































































































































import { namespace } from "vuex-class";
import { Component, Vue, Prop } from "vue-property-decorator";
import rest from "@/rest";
import { l3draggable, l3droppable } from "@/directives/l3dragDrop";
// components
import MarkdownViewer from "../Shared/MarkdownViewer.vue";
import MarkdownEditor from "../Shared/MarkdownEditor.vue";
import ImageWithDnD from "../Shared/ImageWithDnD.vue";
// interfaces
import { ExerciseVM } from "@/interfaces/ExerciseVM";
import { CourseElementVm, CustomTranslationType, QuizOptionVm } from "@/interfaces/CourseElementVm";
import { CustomizationVm } from "@/interfaces/CustomizationVm";
import { CcPageVm, InteractiveListAiSuggestionVm } from "@/interfaces/InteractiveListAiSuggestionVm";

const globals = namespace("globals");
const customization = namespace("customization");

@Component({
  components: {
    MarkdownViewer,
    MarkdownEditor,
    ImageWithDnD
  },
  directives: { l3draggable, l3droppable }
})
export default class FlipCardListElement extends Vue {
  @Prop() element!: CourseElementVm;
  @customization.Getter customizationSettings!: CustomizationVm;
  @globals.Getter selectedExercise!: ExerciseVM;
  @globals.Getter selectedModulePage!: CcPageVm | null;
  @globals.Getter isMasterLanguageCode!: boolean;

  isBusy = false;
  enableCarousel = true;
  showMarkdownEditor: boolean = false;
  selectedOption: QuizOptionVm | null = null;

  // Carousel / flip state
  activeSlide: number = 0;
  flipStates: Record<number, boolean> = {};

  // AI dialog state
  showAiDialog: boolean = false;
  aiPrompt: string = "";

  async onAddOption() {
    // Ensure the options array exists on the element
    if (!this.element.options) {
      (this.element as any).options = [];
    }

    this.enableCarousel = false; // temporarily disable carousel to prevent UI issues during array mutation

    // Add a new option with sensible defaults
    const newOption: QuizOptionVm = {
      id: this.element.options!.length + 1,
      guid: this.$globalHelper.getNewGuid(),
      customTranslation: CustomTranslationType.Master,
      title: "",
      text: "",
      resolution: "",
      isRight: false, // This property is not relevant for a flip card list, but we include it for consistency
      contentUrl: '',
      fillContainer: true,
      horizontalAlign: '',
      prompt: '',
      changePrompt: '',
      altText: '',
      imageLibrarySearchText: '',
    };

    this.element.options!.push(newOption);
    // ensure flip state for new option is false and jump to it
    await this.$globalHelper.delay(10);
    this.enableCarousel = true;
    this.$set(this.flipStates, (this.element.options!.length - 1), false);
    this.activeSlide = this.element.options!.length - 1;
  }

  async onDeleteOption(index: number) {
    if (!this.element.options) return;
    this.element.options.splice(index, 1);
    // rebuild flipStates to keep indexes aligned
    await this.$globalHelper.delay(10);
    const old = { ...this.flipStates };
    this.flipStates = {};
    this.element.options!.forEach((_, idx) => {
      this.$set(this.flipStates, idx, !!old[idx]);
    });
    // adjust activeSlide if needed
    if (this.activeSlide >= this.element.options!.length) {
      this.activeSlide = Math.max(0, this.element.options!.length - 1);
    }
  }

  onToggleFillContainer(item: QuizOptionVm) {
    if (!item) return;
    // ensure reactive toggle
    // const current = (item as any).fillContainer;
    // this.$set(item, 'fillContainer', !current);
    item.fillContainer = !item.fillContainer;
  }

  onOpenMdEditor(item: QuizOptionVm) {
    this.selectedOption = item;
    this.showMarkdownEditor = true;
  }

  toggleFlip(index: number) {
    this.$set(this.flipStates, index, !this.flipStates[index]);
  }

  isFlipped(index: number): boolean {
    return !!this.flipStates[index];
  }

  get markdownEditorContent(): string {
    if (!this.selectedOption) return '';
    const idx = this.element.options ? this.element.options.indexOf(this.selectedOption) : -1;
    const editingResolution = idx >= 0 ? !!this.flipStates[idx] : false;
    return editingResolution ? (this.selectedOption.resolution || '') : (this.selectedOption.text || '');
  }

  set markdownEditorContent(val: string) {
    if (!this.selectedOption) return;
    const idx = this.element.options ? this.element.options.indexOf(this.selectedOption) : -1;
    const editingResolution = idx >= 0 ? !!this.flipStates[idx] : false;
    if (editingResolution) this.selectedOption.resolution = val;
    else this.selectedOption.text = val;
  }

  onCopyToClipboard() {
    if (!this.element.options) return;
    let combinedText = this.element.title ? this.element.title + "\n\n" : "";
    combinedText += this.element.options.map((o, idx) => {
      // const title = (o.title || '').toString().trim();
      const title = 'Punkt';
      const text = (o.text || '').toString().trim();
      const resolution = (o.resolution || '').toString().trim();
      const numberedTitle = title ? `${idx + 1}. ${title}` : '';
      if (numberedTitle && text && resolution) return `${numberedTitle}\nVorderseite:\n${text}\nRückseite:\n${resolution}`;
      return numberedTitle || text || resolution;
    }).filter(Boolean).join('\n\n');
    this.$globalHelper.copyToClipboard(combinedText, false);
  }

  async onOpenAiDialog(): Promise<void> {
    this.aiPrompt = "";
    this.showAiDialog = true;

    await this.$globalHelper.delay(20);
    const ref: any = this.$refs.aiPromptTextarea;
    if (!ref) return;
    // Try component focus first
    if (typeof ref.focus === 'function') {
      try { ref.focus(); return; } catch (e) {}
    }

    // Fallback to underlying textarea element
    const el = (ref.$el && ref.$el.querySelector) ? ref.$el.querySelector('textarea') : (ref.querySelector ? ref.querySelector('textarea') : null);
    if (el && typeof el.focus === 'function') el.focus();
  }

  onCloseAiDialog(): void {
    this.showAiDialog = false;
  }

  async onCreateFlipCardList(): Promise<void> {
    this.isBusy = true;
    try {
      // Build known answers from existing options
      // const knownAnswers = (this.element.options ?? [])
      //   .filter(o => (o.text ?? '').trim() !== '')
      //   .map(o => o.text)
      //   .join(', ');

      const params: InteractiveListAiSuggestionVm = {
        page: this.selectedModulePage,
        title: this.element.title,
        contentPrompt: this.aiPrompt ?? "Keine weiteren Informationen.",
        elementList: this.element.options ?? []
      };

      const response: InteractiveListAiSuggestionVm = await rest.url("contentManager/suggestAiFlipCardList").post(params);
      if (response) {
        this.enableCarousel = false; // temporarily disable carousel to prevent UI issues during array mutation
        this.element.title = response.title;

        // replace existing options with response
        this.element.options = [];
        response.elementList!.forEach((opt: any, idx: number) => {
          this.element.options!.push(<QuizOptionVm>{
            id: idx + 1,
            guid: this.$globalHelper.getNewGuid(),
            customTranslation: CustomTranslationType.Master,
            title: opt.title || (`Punkt ${idx + 1}`),
            text: opt.text,
            resolution: opt.resolution || '',
            isRight: false,
            selected: false,
            contentUrl: opt.contentUrl || '',
            fillContainer: opt.fillContainer,
            horizontalAlign: opt.horizontalAlign !== undefined ? opt.horizontalAlign : 'end',
            prompt: opt.prompt || '',
            changePrompt: opt.changePrompt || '',
            altText: opt.altText || '',
            imageLibrarySearchText: opt.imageLibrarySearchText || ''
          });
        });

        // ensure sequential ids
        this.element.options.forEach((o: any, i: number) => o.id = i + 1);
        this.element.guid = this.$globalHelper.getNewGuid(); // since list has changed, invalidate old state by changing guid, so the user have to click all options again
        // select first option and reset flip states        await this.$globalHelper.delay(10);
        this.activeSlide = 0;
        this.flipStates = {};
        // ensure flip state for new options is false (use index keys for reactivity)
        this.element.options.forEach((o: any, idx: number) => {
          this.$set(this.flipStates, idx, false);
        });

        await this.$globalHelper.delay(10);
        this.enableCarousel = true;
        this.onCloseAiDialog();
      }
    } catch (err) {
      console.error("Error creating AI content list:", err);
      // this.$store.commit("ux/SB_FAILURE", { message: "Fehler bei KI-Anfrage.", timeout: 5000 });
    } finally {
      this.isBusy = false;
    }
  }

}
